import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  ArrowRight,
  Building2,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
  Download,
  ShieldAlert,
} from 'lucide-react'
import { Line, LineChart } from 'recharts'
import { Badge, Input, Select } from '@/components/ui'
import type { BadgeVariant } from '@/components/ui'
import {
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableHeadCell,
  DataTableRow,
} from '@/components/common/DataTable'
import { EmptyState } from '@/components/common/EmptyState'
import { TableSkeleton } from '@/components/common/TableSkeleton'
import { TablePaginationBar } from '@/components/common/TablePaginationBar'
import { usePermissions } from '@/hooks/usePermissions'
import { cn } from '@/lib/cn'
import type { OrganizationDashboard, OrganizationDashboardCompany } from '@/types/dashboard.types'
import { DashboardDetailModal, DashboardTile } from './DashboardTile'
import { ATTENDANCE_WARN, attendanceTone, downloadCsv } from './dashboardFormat'

type Severity = 'red' | 'amber' | 'none'

const severityRank: Record<Severity, number> = { red: 2, amber: 1, none: 0 }

interface TriageRow {
  companyId: string
  name: string
  code?: string
  status: string
  memberCount: number
  noShowsToday: number
  pendingApprovals: number
  pendingLeaves: number
  pendingCorrections: number
  unconfirmed: number
  exceptionCount: number
  clockedInNow: number
  /** BE attendancePct: attended / (people due by now, not on leave); null when nobody was expected. */
  attendancePct: number | null
  weekAttendance: Array<number | null>
  severity: Severity
}

function severityOf(
  c: OrganizationDashboardCompany,
  unconfirmed: number,
  pct: number | null
): Severity {
  if (c.noShowsToday > 0 || (pct !== null && pct < ATTENDANCE_WARN)) return 'red'
  if (c.pendingApprovals > 0 || unconfirmed > 0) return 'amber'
  return 'none'
}

function toTriageRow(c: OrganizationDashboardCompany): TriageRow {
  const unconfirmed = c.unconfirmedShiftsToday ?? 0
  const attendancePct = c.attendancePct ?? null
  return {
    companyId: c.companyId,
    name: c.name,
    code: c.code,
    status: c.status,
    memberCount: c.memberCount,
    noShowsToday: c.noShowsToday,
    pendingApprovals: c.pendingApprovals,
    pendingLeaves: c.pendingLeaves,
    pendingCorrections: c.pendingCorrections,
    unconfirmed,
    exceptionCount: c.exceptionCount,
    clockedInNow: c.clockedInNow,
    attendancePct,
    weekAttendance: c.weekAttendance ?? [],
    severity: severityOf(c, unconfirmed, attendancePct),
  }
}

type SortKey =
  | 'name'
  | 'noShowsToday'
  | 'pendingApprovals'
  | 'unconfirmed'
  | 'attendancePct'
  | 'clockedInNow'
  | 'severity'

type QuickFilter = 'red' | 'noshows' | 'pending' | null

function sortValue(row: TriageRow, key: SortKey): number {
  switch (key) {
    case 'noShowsToday':
      return row.noShowsToday
    case 'pendingApprovals':
      return row.pendingApprovals
    case 'unconfirmed':
      return row.unconfirmed
    case 'clockedInNow':
      return row.clockedInNow
    case 'attendancePct':
      return row.attendancePct ?? -1
    case 'severity':
      return severityRank[row.severity]
    default:
      return 0
  }
}

function compareRows(a: TriageRow, b: TriageRow, key: SortKey, dir: 'asc' | 'desc'): number {
  let diff: number
  if (key === 'name') {
    diff = a.name.localeCompare(b.name)
  } else {
    diff = sortValue(a, key) - sortValue(b, key)
  }
  const primary = dir === 'asc' ? diff : -diff
  if (primary !== 0) return primary
  // Stable tiebreak: worst exceptions first, then name.
  if (a.exceptionCount !== b.exceptionCount) return b.exceptionCount - a.exceptionCount
  return a.name.localeCompare(b.name)
}

function statusBadgeVariant(status: string): BadgeVariant {
  switch (status.toLowerCase()) {
    case 'active':
      return 'success'
    case 'suspended':
    case 'inactive':
      return 'warning'
    case 'archived':
      return 'neutral'
    default:
      return 'neutral'
  }
}

/** A clickable count that deep-links when there's something to act on and the user may. */
function CountCell({
  count,
  to,
  enabled,
  emphasize,
  title,
}: {
  count: number
  to?: string
  enabled?: boolean
  emphasize?: boolean
  title?: string
}): React.ReactElement {
  const tone = emphasize && count > 0 ? 'font-semibold text-rose-600 dark:text-rose-400' : ''
  if (count > 0 && to && enabled) {
    return (
      <Link
        to={to}
        title={title}
        className={cn(
          'tabular-nums underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary',
          tone || 'text-gray-900 dark:text-gray-100'
        )}
      >
        {count}
      </Link>
    )
  }
  return (
    <span
      title={title}
      className={cn('tabular-nums', count === 0 ? 'text-gray-300 dark:text-gray-600' : tone)}
    >
      {count}
    </span>
  )
}

function Sparkline({ data }: { data: Array<number | null> }): React.ReactElement {
  // null = a day still ahead or with nobody scheduled: a gap, not a 0.
  if (!data.some((v) => v !== null)) {
    return <span className="text-gray-300 dark:text-gray-600">—</span>
  }
  const points = data.map((v, i) => ({ i, v }))
  return (
    <LineChart
      width={64}
      height={24}
      data={points}
      margin={{ top: 4, right: 2, bottom: 4, left: 2 }}
    >
      <Line
        type="monotone"
        dataKey="v"
        stroke="currentColor"
        strokeWidth={1.5}
        // Days ahead are gaps; a dot keeps a lone day (e.g. Monday) visible.
        dot={{ r: 1.5, fill: 'currentColor', strokeWidth: 0 }}
        connectNulls={false}
        isAnimationActive={false}
      />
    </LineChart>
  )
}

function SortHeader({
  label,
  sortKey,
  active,
  dir,
  align = 'right',
  onSort,
}: {
  label: string
  sortKey: SortKey
  active: boolean
  dir: 'asc' | 'desc'
  align?: 'left' | 'right'
  onSort: (key: SortKey) => void
}): React.ReactElement {
  const Icon = !active ? ChevronsUpDown : dir === 'asc' ? ChevronUp : ChevronDown
  return (
    <DataTableHeadCell align={align}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={cn(
          'inline-flex items-center gap-1 uppercase tracking-wide transition-colors hover:text-gray-700 dark:hover:text-gray-200',
          align === 'right' && 'flex-row-reverse',
          active && 'text-gray-700 dark:text-gray-200'
        )}
      >
        {label}
        <Icon className={cn('h-3 w-3', !active && 'opacity-40')} aria-hidden />
      </button>
    </DataTableHeadCell>
  )
}

const QUICK_FILTERS: Array<{ key: Exclude<QuickFilter, null>; label: string }> = [
  { key: 'red', label: 'Critical' },
  { key: 'noshows', label: 'Has no-shows' },
  { key: 'pending', label: 'Has pending' },
]

const DASHBOARD_PAGE_SIZE_OPTIONS = [10, 25, 50] as const

/**
 * Companies with something to act on today: no-shows, low attendance,
 * pending approvals, unconfirmed shifts. Healthy companies stay folded away
 * on the card and open by default in the expanded view.
 */
function CompanyTriage({
  data,
  isLoading,
  expanded = false,
}: {
  data: OrganizationDashboard | undefined
  isLoading: boolean
  expanded?: boolean
}): React.ReactElement {
  const { can } = usePermissions()
  const canAttendance = can('attendance', 'read')
  const canLeave = can('leave', 'read')
  const canShifts = can('shifts', 'read')
  const canTimeCorrections = can('time_corrections', 'read')
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [quickFilter, setQuickFilter] = useState<QuickFilter>(null)
  const [showHealthy, setShowHealthy] = useState(expanded)
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({
    key: 'severity',
    dir: 'desc',
  })
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(expanded ? 25 : 10)

  const rows = useMemo<TriageRow[]>(
    () => (data?.companyBreakdown ?? []).map(toTriageRow),
    [data?.companyBreakdown]
  )

  const statusOptions = useMemo(() => {
    const set = new Set(rows.map((r) => r.status))
    return [
      { value: 'all', label: 'All statuses' },
      ...Array.from(set).map((s) => ({
        value: s,
        label: s.charAt(0).toUpperCase() + s.slice(1),
      })),
    ]
  }, [rows])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    const next = rows.filter((r) => {
      const matchesStatus = statusFilter === 'all' || r.status === statusFilter
      const matchesSearch =
        q === '' || r.name.toLowerCase().includes(q) || (r.code?.toLowerCase().includes(q) ?? false)
      const matchesQuick =
        quickFilter === null ||
        (quickFilter === 'red' && r.severity === 'red') ||
        (quickFilter === 'noshows' && r.noShowsToday > 0) ||
        (quickFilter === 'pending' && r.pendingApprovals > 0)
      return matchesStatus && matchesSearch && matchesQuick
    })
    next.sort((a, b) => compareRows(a, b, sort.key, sort.dir))
    return next
  }, [rows, search, statusFilter, quickFilter, sort])

  const attention = useMemo(() => filtered.filter((r) => r.severity !== 'none'), [filtered])
  const healthy = useMemo(() => filtered.filter((r) => r.severity === 'none'), [filtered])
  const visibleRows = showHealthy ? filtered : attention
  const totalPages = Math.max(1, Math.ceil(visibleRows.length / limit))
  // Back to page 1 when the filters change (adjusted during render, not in an
  // effect), and clamp when a background refetch shrinks the list.
  const filterKey = JSON.stringify([quickFilter, search, showHealthy, sort, statusFilter])
  const [pageFilterKey, setPageFilterKey] = useState(filterKey)
  if (pageFilterKey !== filterKey) {
    setPageFilterKey(filterKey)
    setPage(1)
  }
  const currentPage = Math.min(page, totalPages)
  const paginatedRows = useMemo(
    () => visibleRows.slice((currentPage - 1) * limit, currentPage * limit),
    [limit, currentPage, visibleRows]
  )
  const hasFilters = search.trim() !== '' || statusFilter !== 'all' || quickFilter !== null

  function toggleSort(key: SortKey): void {
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: key === 'name' ? 'asc' : 'desc' }
    )
  }

  function handleExport(): void {
    downloadCsv({
      filename: 'org-companies',
      header: [
        'Company',
        'Code',
        'Status',
        'Members',
        'No-shows',
        'Pending leaves',
        'Pending corrections',
        'Unconfirmed',
        'Attendance %',
        'Clocked in',
      ],
      rows: filtered.map((r) => [
        r.name,
        r.code ?? '',
        r.status,
        r.memberCount,
        r.noShowsToday,
        r.pendingLeaves,
        r.pendingCorrections,
        r.unconfirmed,
        r.attendancePct ?? '',
        r.clockedInNow,
      ]),
      noun: ['company', 'companies'],
    })
  }

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 pb-3">
        <div className="flex flex-wrap items-center gap-1.5">
          {!isLoading && rows.length > 0 ? (
            <div
              className="flex flex-wrap items-center gap-1.5"
              role="group"
              aria-label="Quick filters"
            >
              {QUICK_FILTERS.map((chip) => {
                const active = quickFilter === chip.key
                return (
                  <button
                    key={chip.key}
                    type="button"
                    onClick={() => setQuickFilter(active ? null : chip.key)}
                    aria-pressed={active}
                    className={cn(
                      'rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
                      active
                        ? 'border-primary bg-primary/10 text-primary dark:border-sky-400/60 dark:bg-sky-400/10 dark:text-sky-300'
                        : 'border-gray-200 text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-700/50'
                    )}
                  >
                    {chip.label}
                  </button>
                )
              })}
            </div>
          ) : null}
        </div>
        <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:flex-nowrap">
          <Input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filter companies…"
            aria-label="Filter companies"
            className="h-9 min-w-0 flex-1 sm:w-44 sm:flex-none"
          />
          <div className="w-36 shrink-0 sm:w-40">
            <Select
              options={statusOptions}
              value={statusFilter}
              onChange={(v) => setStatusFilter(v || 'all')}
              clearable={false}
              aria-label="Filter by status"
            />
          </div>
          <button
            type="button"
            onClick={handleExport}
            disabled={isLoading || filtered.length === 0}
            className="flex h-9 shrink-0 items-center gap-1.5 rounded-lg border border-gray-200 px-2.5 text-xs font-medium text-gray-600 transition hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-700/50"
          >
            <Download className="h-3.5 w-3.5" aria-hidden />
            Export
          </button>
        </div>
      </div>
      {isLoading ? (
        <TableSkeleton rows={5} cols={7} className="p-2" />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={Building2}
          title={hasFilters ? 'No companies match your filters' : 'No companies yet'}
          description={
            hasFilters
              ? 'Try clearing the search, status, or quick filters.'
              : 'Create a company to get started.'
          }
          className="m-4"
        />
      ) : attention.length === 0 && !showHealthy ? (
        <EmptyState
          icon={CheckCircle2}
          title="All clear — nothing needs attention"
          description="No no-shows, pending approvals, or unconfirmed shifts across your companies."
          className="m-4"
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse text-[13px] leading-5">
            <DataTableHead>
              <tr>
                <DataTableHeadCell align="left" className="pl-[26px]">
                  <button
                    type="button"
                    onClick={() => toggleSort('name')}
                    className={cn(
                      'inline-flex items-center gap-1 uppercase tracking-wide transition-colors hover:text-gray-700 dark:hover:text-gray-200',
                      sort.key === 'name' && 'text-gray-700 dark:text-gray-200'
                    )}
                  >
                    Company
                    {(() => {
                      const Icon =
                        sort.key !== 'name'
                          ? ChevronsUpDown
                          : sort.dir === 'asc'
                            ? ChevronUp
                            : ChevronDown
                      return (
                        <Icon
                          className={cn('h-3 w-3', sort.key !== 'name' && 'opacity-40')}
                          aria-hidden
                        />
                      )
                    })()}
                  </button>
                </DataTableHeadCell>
                <SortHeader
                  label="No-shows"
                  sortKey="noShowsToday"
                  active={sort.key === 'noShowsToday'}
                  dir={sort.dir}
                  onSort={toggleSort}
                />
                <SortHeader
                  label="Pending"
                  sortKey="pendingApprovals"
                  active={sort.key === 'pendingApprovals'}
                  dir={sort.dir}
                  onSort={toggleSort}
                />
                <SortHeader
                  label="Unconfirmed"
                  sortKey="unconfirmed"
                  active={sort.key === 'unconfirmed'}
                  dir={sort.dir}
                  onSort={toggleSort}
                />
                <SortHeader
                  label="Attendance"
                  sortKey="attendancePct"
                  active={sort.key === 'attendancePct'}
                  dir={sort.dir}
                  onSort={toggleSort}
                />
                <DataTableHeadCell align="right">7-day</DataTableHeadCell>
                <SortHeader
                  label="Clocked in"
                  sortKey="clockedInNow"
                  active={sort.key === 'clockedInNow'}
                  dir={sort.dir}
                  onSort={toggleSort}
                />
                <DataTableHeadCell align="right">Open</DataTableHeadCell>
              </tr>
            </DataTableHead>
            <DataTableBody>
              {paginatedRows.map((row) => {
                const pendingTitle = `${row.pendingLeaves} leave${row.pendingLeaves === 1 ? '' : 's'} · ${row.pendingCorrections} correction${row.pendingCorrections === 1 ? '' : 's'}`
                // Route Pending to the queue that actually has items; company page when mixed.
                const pendingTo =
                  row.pendingLeaves > 0 && row.pendingCorrections === 0 && canLeave
                    ? `/leaves?companyId=${row.companyId}`
                    : row.pendingCorrections > 0 && row.pendingLeaves === 0 && canTimeCorrections
                      ? `/companies/${row.companyId}/attendance/time-corrections`
                      : `/companies/${row.companyId}`
                return (
                  <DataTableRow key={row.companyId}>
                    <DataTableCell>
                      <div className="flex items-center gap-2.5">
                        <span
                          className={cn(
                            'h-8 w-1 shrink-0 rounded-full',
                            row.severity === 'red'
                              ? 'bg-accent'
                              : row.severity === 'amber'
                                ? 'bg-amber-400'
                                : 'bg-gray-200 dark:bg-gray-600'
                          )}
                          aria-hidden
                        />
                        <div className="min-w-0">
                          <Link
                            to={`/companies/${row.companyId}`}
                            className="truncate font-medium text-gray-900 hover:text-primary hover:underline dark:text-gray-100 dark:hover:text-sky-300"
                          >
                            {row.name}
                          </Link>
                          {row.code ? (
                            <p className="truncate text-xs text-gray-400 dark:text-gray-500">
                              {row.code}
                            </p>
                          ) : null}
                        </div>
                        <Badge
                          variant={statusBadgeVariant(row.status)}
                          className="ml-1 hidden text-[10px] sm:inline-flex"
                        >
                          {row.status}
                        </Badge>
                      </div>
                    </DataTableCell>
                    <DataTableCell align="right">
                      <CountCell
                        count={row.noShowsToday}
                        to={`/companies/${row.companyId}/attendance`}
                        enabled={canAttendance}
                        emphasize
                      />
                    </DataTableCell>
                    <DataTableCell align="right">
                      <CountCell
                        count={row.pendingApprovals}
                        to={pendingTo}
                        enabled
                        title={pendingTitle}
                      />
                    </DataTableCell>
                    <DataTableCell align="right">
                      <CountCell
                        count={row.unconfirmed}
                        to={`/companies/${row.companyId}/shifts`}
                        enabled={canShifts}
                      />
                    </DataTableCell>
                    <DataTableCell align="right">
                      <span
                        className={cn(
                          'font-medium tabular-nums',
                          attendanceTone(row.attendancePct)
                        )}
                      >
                        {row.attendancePct === null ? '—' : `${row.attendancePct}%`}
                      </span>
                    </DataTableCell>
                    <DataTableCell align="right">
                      <div className="flex justify-end text-primary dark:text-sky-400">
                        <Sparkline data={row.weekAttendance} />
                      </div>
                    </DataTableCell>
                    <DataTableCell align="right">
                      <span className="tabular-nums text-gray-600 dark:text-gray-300">
                        {row.clockedInNow}
                        <span className="text-gray-400 dark:text-gray-500">/{row.memberCount}</span>
                      </span>
                    </DataTableCell>
                    <DataTableCell align="right">
                      <Link
                        to={`/companies/${row.companyId}`}
                        className="inline-flex items-center text-gray-400 transition-colors hover:text-primary"
                        aria-label={`Open ${row.name}`}
                      >
                        <ArrowRight className="h-4 w-4" aria-hidden />
                      </Link>
                    </DataTableCell>
                  </DataTableRow>
                )
              })}
            </DataTableBody>
          </table>
        </div>
      )}

      {!isLoading && visibleRows.length > limit ? (
        <div className="border-t border-gray-100 px-5 py-3 dark:border-gray-700/70">
          <TablePaginationBar
            page={currentPage}
            limit={limit}
            total={visibleRows.length}
            totalPages={totalPages}
            onPageChange={setPage}
            pageSizeOptions={DASHBOARD_PAGE_SIZE_OPTIONS}
            onLimitChange={setLimit}
          />
        </div>
      ) : null}

      {!isLoading && healthy.length > 0 ? (
        <button
          type="button"
          onClick={() => setShowHealthy((v) => !v)}
          className="flex w-full items-center justify-center gap-1.5 rounded-b-[var(--radius-panel)] border-t border-gray-100 px-4 py-2.5 text-xs font-medium text-gray-500 transition hover:bg-gray-50 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700/40"
        >
          {showHealthy ? (
            <>
              <ChevronUp className="h-3.5 w-3.5" aria-hidden />
              Hide {healthy.length} healthy {healthy.length === 1 ? 'company' : 'companies'}
            </>
          ) : (
            <>
              <ChevronDown className="h-3.5 w-3.5" aria-hidden />
              Show {healthy.length} healthy {healthy.length === 1 ? 'company' : 'companies'}
            </>
          )}
        </button>
      ) : null}
    </>
  )
}

function useAttentionCount(data: OrganizationDashboard | undefined): number {
  return useMemo(
    () =>
      (data?.companyBreakdown ?? []).map(toTriageRow).filter((r) => r.severity !== 'none').length,
    [data?.companyBreakdown]
  )
}

export function NeedsAttentionCard({
  data,
  isLoading,
  onExpand,
}: {
  data: OrganizationDashboard | undefined
  isLoading: boolean
  onExpand: () => void
}): React.ReactElement {
  const totalAttention = useAttentionCount(data)
  return (
    <DashboardTile
      title="Needs attention now"
      icon={ShieldAlert}
      subtitle="Companies with no-shows, low attendance, pending approvals or unconfirmed shifts"
      onExpand={onExpand}
      actions={
        !isLoading ? (
          <Badge variant={totalAttention > 0 ? 'danger' : 'success'} size="sm">
            {totalAttention > 0 ? totalAttention : 'All clear'}
          </Badge>
        ) : null
      }
    >
      <CompanyTriage data={data} isLoading={isLoading} />
    </DashboardTile>
  )
}

export function NeedsAttentionModal({
  open,
  data,
  isLoading,
  onClose,
}: {
  open: boolean
  data: OrganizationDashboard | undefined
  isLoading: boolean
  onClose: () => void
}): React.ReactElement | null {
  if (!open) return null
  return (
    <DashboardDetailModal
      open
      onClose={onClose}
      title="Company triage"
      subtitle="Every visible company, worst first. Counts link straight to the queue that clears them."
    >
      <div className="-mx-5 -mb-5">
        <CompanyTriage data={data} isLoading={isLoading} expanded />
      </div>
    </DashboardDetailModal>
  )
}
